import React, { useCallback, useMemo } from 'react'
import {
  Box,
  Chip,
  Typography,
  useTheme,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import {
  Timeline as TimelineIcon,
  Build as BuildIcon,
  Edit as PlanIcon,
  CheckCircle,
  PhotoCamera as PhotoCameraIcon,
  Flag as FlagIcon,
  Visibility as ViewIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material'
import { OtwAgGridReact as AgGridReact } from '@/components/AgGrid/OtwAgGridReact'
import {
  CellDoubleClickedEvent,
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { MobileRecordCard } from 'components/MobileRecordCard'
import { MobileRecordList } from 'components/MobileRecordList'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import type { StatusTone } from 'core/theme.tokens'
import {
  muiChipColorToStatusTone,
  transportReadyLaneTone,
  transportTruckStatusTone,
} from '../../utils/mobileStatusTones'
import CopyGridRowButton from '../CopyGridRowButton'
import {
  formatVehiclesV2RowForClipboard,
  getVehiclesV2TruckStatusLabel,
  copyTextToClipboard,
} from '../../utils/gridRowClipboard'

export const SHOP_TRANSPORT_COMPANY_ID = 'shop'
export const CUSTOMER_PICKUP_COMPANY_ID = 'customer_pickup'

function formatGridDate(date?: string | null): string {
  if (!date) return '—'
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function dateToSortMs(date?: string | null): number | null {
  if (!date) return null
  const ms = new Date(date).getTime()
  return Number.isNaN(ms) ? null : ms
}

function getCellDisplayTextForCopy(event: CellDoubleClickedEvent<VehiclesV2Row>): string {
  const { value, colDef, data, node, api, column, context } = event
  if (!colDef) return ''

  if (typeof colDef.valueFormatter === 'function') {
    const formatted = colDef.valueFormatter({
      value,
      data,
      node,
      colDef,
      column,
      api,
      context,
    } as ValueFormatterParams<VehiclesV2Row>)
    if (formatted != null && String(formatted).trim() !== '') {
      return String(formatted)
    }
  }

  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (value == null) return ''
  return String(value)
}

const NON_COPYABLE_COLUMN_HEADERS = new Set(['Actions'])

export function formatTransporterDisplayName(
  companyId: string,
  groupName?: string | null
): string {
  if (companyId === SHOP_TRANSPORT_COMPANY_ID) return 'Shop transport'
  if (companyId === CUSTOMER_PICKUP_COMPANY_ID) return 'Customer pickup'
  const trimmed = groupName?.trim()
  return trimmed || companyId
}

export interface VehiclesV2Row {
  id: string
  inventoryVehicleId: string | null
  vehicle: string
  stock: string
  tier: string
  vin: string
  estimatedRepairCost: number | null
  fundedDate: string | null
  paidDate: string | null
  customer: string
  inventoryStatus: string
  inventoryStatusChipColor?: 'default' | 'info' | 'error' | 'warning'
  location: string
  status: string
  flagged: boolean
  canToggleFlag: boolean
  hasAnyLegPhotos: boolean
  readyForPickup: boolean
  transporters: string[]
  /** Present when the transport truck has an active repair-truck at its current location. */
  repairTruckId: string | null
}

interface VehiclesV2GridProps {
  rows: VehiclesV2Row[]
  isLoading: boolean
  onOpenTimeline?: (truckId: string) => void
  onOpenRepairTimeline?: (truckId: string) => void
  onOpenShipmentPlan?: (truckId: string) => void
  onOpenLegPhotos?: (truckId: string) => void
  onToggleFlag?: (truckId: string, nextFlagged: boolean) => void
  isTogglingFlag?: boolean
  onOpenTransportTruck?: (truckId: string) => void
  onOpenInventoryForm?: (inventoryVehicleId: string) => void
}

interface VehiclesActionsMenuProps {
  row: VehiclesV2Row
  onOpenTimeline?: (truckId: string) => void
  onOpenRepairTimeline?: (truckId: string) => void
  onOpenShipmentPlan?: (truckId: string) => void
  onOpenLegPhotos?: (truckId: string) => void
  onToggleFlag?: (truckId: string, nextFlagged: boolean) => void
  onOpenTransportTruck?: (truckId: string) => void
  onOpenInventoryForm?: (inventoryVehicleId: string) => void
  isTogglingFlag: boolean
}

const VehiclesActionsMenu: React.FC<VehiclesActionsMenuProps> = ({
  row,
  onOpenTimeline,
  onOpenRepairTimeline,
  onOpenShipmentPlan,
  onOpenLegPhotos,
  onToggleFlag,
  onOpenTransportTruck,
  onOpenInventoryForm,
  isTogglingFlag,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const isOpen = Boolean(anchorEl)
  const truckId = row.id

  const closeMenu = () => setAnchorEl(null)

  return (
    <>
      <Tooltip title="Actions">
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={isOpen} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            onOpenTransportTruck?.(truckId)
            closeMenu()
          }}
        >
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View transport card</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={!row.inventoryVehicleId}
          onClick={() => {
            if (!row.inventoryVehicleId) return
            onOpenInventoryForm?.(row.inventoryVehicleId)
            closeMenu()
          }}
        >
          <ListItemIcon><PlanIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Open inventory form</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            onOpenTimeline?.(truckId)
            closeMenu()
          }}
        >
          <ListItemIcon><TimelineIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Open timeline</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={row.status !== 'repair'}
          onClick={() => {
            if (row.status === 'repair') onOpenRepairTimeline?.(truckId)
            closeMenu()
          }}
        >
          <ListItemIcon><BuildIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Open repair timeline</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            onOpenShipmentPlan?.(truckId)
            closeMenu()
          }}
        >
          <ListItemIcon><PlanIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Open shipment plan</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={!row.hasAnyLegPhotos}
          onClick={() => {
            if (!row.hasAnyLegPhotos) return
            onOpenLegPhotos?.(truckId)
            closeMenu()
          }}
        >
          <ListItemIcon><PhotoCameraIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View photos</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={!row.canToggleFlag || isTogglingFlag}
          onClick={() => {
            if (row.canToggleFlag && !isTogglingFlag) {
              onToggleFlag?.(truckId, !row.flagged)
            }
            closeMenu()
          }}
        >
          <ListItemIcon><FlagIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{row.flagged ? 'Unflag truck' : 'Flag truck'}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  )
}

const VehiclesV2Grid: React.FC<VehiclesV2GridProps> = ({
  rows,
  isLoading,
  onOpenTimeline,
  onOpenRepairTimeline,
  onOpenShipmentPlan,
  onOpenLegPhotos,
  onToggleFlag,
  isTogglingFlag = false,
  onOpenTransportTruck,
  onOpenInventoryForm,
}) => {
  const theme = useTheme()
  const isMobile = useIsMobile()
  const { showSuccess, showError } = useSnackbar()
  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
    []
  )
  const getStatusLabel = getVehiclesV2TruckStatusLabel

  const statusRenderer = (params: ICellRendererParams<VehiclesV2Row>) => {
    const value = params.value as VehiclesV2Row['status']
    const isAwaitingTransport = value === 'assigned' || value === 'awaiting_transport'
    const color =
      value === 'delivered'
        ? 'success'
        : value === 'in_transit'
          ? 'primary'
          : isAwaitingTransport
            ? 'info'
            : value === 'repair'
              ? 'error'
              : 'default'
    const label = getStatusLabel(value)
    return (
      <Chip
        label={label}
        size="small"
        color={color}
        sx={{ textTransform: 'capitalize' }}
      />
    )
  }

  const flaggedRenderer = (params: ICellRendererParams<VehiclesV2Row>) => (
    <Chip
      label={params.value ? 'Yes' : 'No'}
      size="small"
      color={params.value ? 'warning' : 'default'}
    />
  )

  const readyForPickupRenderer = (params: ICellRendererParams<VehiclesV2Row>) =>
    params.value ? <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} /> : null

  const customerRenderer = (params: ICellRendererParams<VehiclesV2Row>) => {
    const row = params.data
    if (!row) return null
    return (
      <Typography variant="body2">{row.customer}</Typography>
    )
  }

  const inventoryStatusRenderer = (params: ICellRendererParams<VehiclesV2Row>) => {
    const row = params.data
    if (!row) return null
    return (
      <Chip
        size="small"
        color={row.inventoryStatusChipColor ?? 'default'}
        label={row.inventoryStatus || 'Unknown'}
      />
    )
  }
  const transportersRenderer = (params: ICellRendererParams<VehiclesV2Row>) => {
    const row = params.data
    if (!row) return null
    if (!Array.isArray(row.transporters) || row.transporters.length === 0) {
      return <Typography variant="body2" color="text.secondary">—</Typography>
    }
    return <Typography variant="body2">{row.transporters.join(', ')}</Typography>
  }

  const columnDefs = useMemo<ColDef<VehiclesV2Row>[]>(
    () => [
      {
        colId: 'actions',
        headerName: 'Actions',
        minWidth: 96,
        maxWidth: 96,
        sortable: false,
        filter: false,
        pinned: 'left',
        lockPinned: true,
        suppressMovable: true,
        cellRenderer: (params: ICellRendererParams<VehiclesV2Row>) => {
          const row = params.data
          if (!row) return null
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <CopyGridRowButton
                getCopyText={() =>
                  formatVehiclesV2RowForClipboard(row, (amount) =>
                    currencyFormatter.format(amount),
                  )
                }
              />
              <VehiclesActionsMenu
                row={row}
                onOpenTimeline={onOpenTimeline}
                onOpenRepairTimeline={onOpenRepairTimeline}
                onOpenShipmentPlan={onOpenShipmentPlan}
                onOpenLegPhotos={onOpenLegPhotos}
                onToggleFlag={onToggleFlag}
                onOpenTransportTruck={onOpenTransportTruck}
                onOpenInventoryForm={onOpenInventoryForm}
                isTogglingFlag={isTogglingFlag}
              />
            </Box>
          )
        },
      },
      { field: 'vehicle', headerName: 'Vehicle', minWidth: 210, flex: 1, filter: 'agTextColumnFilter' },
      { field: 'stock', headerName: 'Stock #', minWidth: 110, filter: 'agTextColumnFilter' },
      { field: 'tier', headerName: 'Tier', minWidth: 110, filter: 'agSetColumnFilter' },
      { field: 'vin', headerName: 'VIN', minWidth: 180, filter: 'agTextColumnFilter' },
      {
        field: 'estimatedRepairCost',
        headerName: 'Estimated repair cost',
        minWidth: 190,
        filter: 'agNumberColumnFilter',
        valueFormatter: (params) =>
          typeof params.value === 'number' ? currencyFormatter.format(params.value) : '—',
      },
      {
        field: 'fundedDate',
        headerName: 'Funded date',
        minWidth: 140,
        filter: 'agDateColumnFilter',
        valueGetter: (params) => dateToSortMs(params.data?.fundedDate),
        valueFormatter: (params) => formatGridDate(params.data?.fundedDate),
        filterParams: {
          comparator: (filterLocalDateAtMidnight: Date, cellValue: number | null) => {
            if (cellValue == null) return -1
            const cellDate = new Date(cellValue)
            if (cellDate < filterLocalDateAtMidnight) return -1
            if (cellDate > filterLocalDateAtMidnight) return 1
            return 0
          },
        },
      },
      {
        field: 'paidDate',
        headerName: 'Paid date',
        minWidth: 140,
        filter: 'agDateColumnFilter',
        valueGetter: (params) => dateToSortMs(params.data?.paidDate),
        valueFormatter: (params) => formatGridDate(params.data?.paidDate),
        filterParams: {
          comparator: (filterLocalDateAtMidnight: Date, cellValue: number | null) => {
            if (cellValue == null) return -1
            const cellDate = new Date(cellValue)
            if (cellDate < filterLocalDateAtMidnight) return -1
            if (cellDate > filterLocalDateAtMidnight) return 1
            return 0
          },
        },
      },
      { field: 'customer', headerName: 'Customer', minWidth: 180, filter: 'agSetColumnFilter', cellRenderer: customerRenderer },
      {
        field: 'transporters',
        headerName: 'Transporters',
        minWidth: 220,
        filter: 'agTextColumnFilter',
        cellRenderer: transportersRenderer,
        valueGetter: (params) => (params.data?.transporters ?? []).join(', '),
      },
      { field: 'inventoryStatus', headerName: 'Status', minWidth: 280, filter: 'agSetColumnFilter', cellRenderer: inventoryStatusRenderer },
      { field: 'location', headerName: 'Current location', minWidth: 180, flex: 1, filter: 'agTextColumnFilter' },
      {
        field: 'readyForPickup',
        headerName: 'Ready for pickup',
        minWidth: 150,
        maxWidth: 170,
        cellRenderer: readyForPickupRenderer,
        filter: 'agSetColumnFilter',
      },
      {
        field: 'status',
        headerName: 'Truck status',
        minWidth: 130,
        cellRenderer: statusRenderer,
        filter: 'agSetColumnFilter',
        valueFormatter: (params) => getStatusLabel(params.value),
        filterParams: {
          valueFormatter: (params: { value: string }) => getStatusLabel(params.value),
        },
      },
      { field: 'flagged', headerName: 'Flagged', minWidth: 110, cellRenderer: flaggedRenderer, filter: 'agSetColumnFilter' },
    ],
    [currencyFormatter, onOpenTimeline, onOpenRepairTimeline, onOpenShipmentPlan, onOpenLegPhotos, onToggleFlag, isTogglingFlag, onOpenTransportTruck, onOpenInventoryForm]
  )

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      floatingFilter: true,
    }),
    []
  )

  const handleCellDoubleClicked = useCallback(
    async (event: CellDoubleClickedEvent<VehiclesV2Row>) => {
      const headerName = event.colDef?.headerName ?? ''
      if (NON_COPYABLE_COLUMN_HEADERS.has(headerName)) return

      const text = getCellDisplayTextForCopy(event).trim()
      if (!text) return

      const ok = await copyTextToClipboard(text)
      if (ok) {
        showSuccess('Cell copied to clipboard')
      } else {
        showError('Could not copy to clipboard')
      }
    },
    [showSuccess, showError],
  )

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Loading vehicles...
        </Typography>
      </Box>
    )
  }

  if (rows.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No vehicles match the current filters.
        </Typography>
      </Box>
    )
  }

  const transportLaneLabel = (row: VehiclesV2Row) => {
    if (!row.readyForPickup) return 'Not ready'
    const lanes: string[] = []
    lanes.push('Ready for pickup')
    return lanes[0]
  }

  if (isMobile) {
    return (
      <Box sx={{ p: 1.5 }}>
        <MobileRecordList>
          {rows.map(row => {
            const laneLabel = transportLaneLabel(row)
            const inventoryTone = muiChipColorToStatusTone(row.inventoryStatusChipColor)
            const chips = [
              row.inventoryStatus
                ? { label: row.inventoryStatus, tone: inventoryTone }
                : null,
              { label: laneLabel, tone: transportReadyLaneTone(laneLabel) },
            ].filter(Boolean) as { label: string; tone: StatusTone }[]

            return (
            <MobileRecordCard
              key={row.id}
              title={`${row.stock || '—'} · ${row.vehicle}${row.tier && row.tier !== '—' ? ` · ${row.tier}` : ''}`}
              status={{
                tone: transportTruckStatusTone(row.status),
                label: getStatusLabel(row.status),
              }}
              chips={chips}
              meta={row.location || '—'}
              detail={[row.customer, row.transporters.join(', ')].filter(Boolean).join(' · ') || '—'}
              trailing={
                <VehiclesActionsMenu
                  row={row}
                  onOpenTimeline={onOpenTimeline}
                  onOpenRepairTimeline={onOpenRepairTimeline}
                  onOpenShipmentPlan={onOpenShipmentPlan}
                  onOpenLegPhotos={onOpenLegPhotos}
                  onToggleFlag={onToggleFlag}
                  onOpenTransportTruck={onOpenTransportTruck}
                  onOpenInventoryForm={onOpenInventoryForm}
                  isTogglingFlag={isTogglingFlag}
                />
              }
            />
            )
          })}
        </MobileRecordList>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box
        className={theme.palette.mode === 'dark' ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'}
        sx={{ height: 'calc(100vh - 360px)', minHeight: 420, width: '100%' }}
      >
        <AgGridReact<VehiclesV2Row>
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          animateRows={false}
          suppressRowClickSelection
          onCellDoubleClicked={handleCellDoubleClicked}
        />
      </Box>
    </Box>
  )
}

export default VehiclesV2Grid
